import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  ArrowLeft,
  CheckCircle,
  Spinner
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AFFILIATE_TYPES = [
  { value: 'realtor', label: 'Realtor' },
  { value: 'interior_designer', label: 'Interior Designer' },
  { value: 'contractor', label: 'Basement Contractor' },
  { value: 'home_gym_installer', label: 'Home Gym Installer' },
  { value: 'pool_company', label: 'Pool Company' },
  { value: 'community_influencer', label: 'Community Influencer' },
  { value: 'hoa_leader', label: 'HOA Leader' },
  { value: 'facebook_group_admin', label: 'Facebook Group Admin' },
  { value: 'local_creator', label: 'Local Creator' },
  { value: 'other', label: 'Other' },
];

export default function AffiliateApplyPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    company_name: '',
    website_or_social: '',
    affiliate_type: '',
    audience_description: '',
    why_join: '',
    estimated_hosts: '',
    agreed_to_terms: false,
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.agreed_to_terms) {
      toast.error('Please agree to the terms and conditions');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/affiliate/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          estimated_hosts: parseInt(formData.estimated_hosts) || 0
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to submit application');
      }

      setSubmitted(true);
      toast.success('Application submitted successfully!');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={48} className="text-[#D4AF37]" weight="fill" />
          </div>
          <h1 className="text-3xl font-serif text-white mb-4">Application Submitted!</h1>
          <p className="text-zinc-400 mb-8">
            Thank you for applying to the 100Nook Affiliate Program. Our team will review your application and get back to you soon.
          </p>
          <p className="text-zinc-500 text-sm mb-8">
            You'll receive an email confirmation at <span className="text-white">{formData.email}</span>
          </p>
          <div className="flex flex-col gap-4">
            <Button
              onClick={() => navigate('/affiliate')}
              className="bg-[#D4AF37] text-black hover:bg-[#C4A030]"
            >
              Back to Affiliate Home
            </Button>
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/5"
            >
              Go to 100Nook
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] py-12 px-6">
      <div className="max-w-2xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/affiliate')}
          className="flex items-center gap-2 text-zinc-400 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft size={20} />
          Back to Affiliate Program
        </button>

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl font-serif text-white mb-4">
            Apply to Become an Affiliate
          </h1>
          <p className="text-zinc-400">
            Fill out the form below and our team will review your application.
          </p>
        </div>

        {/* Application Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-[#121212] border border-white/10 p-8 space-y-6">
            <h2 className="text-xl font-semibold text-white mb-4">Personal Information</h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">
                  Full Name *
                </label>
                <Input
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  required
                  className="bg-[#0a0a0a] border-white/10 text-white"
                  placeholder="John Doe"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">
                  Email Address *
                </label>
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="bg-[#0a0a0a] border-white/10 text-white"
                  placeholder="john@example.com"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">
                  Phone Number *
                </label>
                <Input
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="bg-[#0a0a0a] border-white/10 text-white"
                  placeholder="+1 (555) 123-4567"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">
                  Company / Brand
                </label>
                <Input
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  className="bg-[#0a0a0a] border-white/10 text-white"
                  placeholder="Your Company"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">
                Website or Social Media
              </label>
              <Input
                name="website_or_social"
                value={formData.website_or_social}
                onChange={handleChange}
                className="bg-[#0a0a0a] border-white/10 text-white"
                placeholder="https://yourwebsite.com or @yoursocial"
              />
            </div>
          </div>

          <div className="bg-[#121212] border border-white/10 p-8 space-y-6">
            <h2 className="text-xl font-semibold text-white mb-4">Affiliate Details</h2>
            
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">
                What best describes you? *
              </label>
              <select
                name="affiliate_type"
                value={formData.affiliate_type}
                onChange={handleChange}
                required
                className="w-full bg-[#0a0a0a] border border-white/10 text-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
              >
                <option value="">Select an option</option>
                {AFFILIATE_TYPES.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">
                Describe your audience *
              </label>
              <textarea
                name="audience_description"
                value={formData.audience_description}
                onChange={handleChange}
                required
                rows={3}
                className="w-full bg-[#0a0a0a] border border-white/10 text-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                placeholder="Tell us about your network, followers, or client base..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">
                Why do you want to join? *
              </label>
              <textarea
                name="why_join"
                value={formData.why_join}
                onChange={handleChange}
                required
                rows={3}
                className="w-full bg-[#0a0a0a] border border-white/10 text-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                placeholder="Tell us why you'd be a great affiliate partner..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">
                Estimated hosts you can refer *
              </label>
              <Input
                name="estimated_hosts"
                type="number"
                min="1"
                value={formData.estimated_hosts}
                onChange={handleChange}
                required
                className="bg-[#0a0a0a] border-white/10 text-white"
                placeholder="10"
              />
            </div>
          </div>

          <div className="bg-[#121212] border border-white/10 p-8">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="agreed_to_terms"
                checked={formData.agreed_to_terms}
                onChange={handleChange}
                className="w-5 h-5 rounded border-white/10 mt-0.5"
              />
              <span className="text-zinc-400 text-sm">
                I agree to the 100Nook Affiliate Program Terms and Conditions. I understand that my application will be reviewed and approval is not guaranteed. I confirm that all information provided is accurate. *
              </span>
            </label>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#D4AF37] text-black hover:bg-[#C4A030] py-6 text-lg font-semibold"
            data-testid="submit-affiliate-application"
          >
            {loading ? (
              <>
                <Spinner size={20} className="animate-spin mr-2" />
                Submitting...
              </>
            ) : (
              'Submit Application'
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
